// store/slices/formBuilderSlice.ts
import { FormConfigType } from "@/utils/types/store";

export interface FormBuilderState {
  forms: { formId: string; name: string; formData: FormConfigType }[];
  selectedFormId: string | null;
  currentFormData: FormConfigType | null;
  tempFormData: FormConfigType | null;
  isDirty: boolean;
  selectedFormBuilderReportTemplate: string | null;
}

export interface FormBuilderActions {
  setForms: (forms: any[]) => void;
  setSelectedFormId: (formId: string) => void;
  addForm: (form: any) => void;
  updateForm: (formId: string, formData: any) => void;
  deleteForm: (formId: string) => void;
  setCurrentFormData: (data: FormConfigType) => void;
  updateTempFormData: (data: Partial<FormConfigType>) => void;
  saveChanges: () => void;
  discardChanges: () => void;
  resetForm: () => void;
  setSelectedFormBuilderReportTemplate: (templateId: string) => void;
  reorderForms: (newFormsOrder: any[]) => void;
}

export const formBuilderSlice = (
  set: any,
): { data: FormBuilderState; actions: FormBuilderActions } => ({
  data: {
    forms: [],
    selectedFormId: null,
    currentFormData: null,
    tempFormData: null,
    isDirty: false,
    selectedFormBuilderReportTemplate: null,
  },
  actions: {
    setForms: (forms: any[]) => {
      set((state: any) => {
        state.data.forms = forms;
      });
    },
    setSelectedFormId: (formId: string) => {
      set((state: any) => {
        state.data.selectedFormId = formId;
        const selectedForm = (state.data.forms || []).find(
          (f: any) => f.formId === formId,
        );
        state.data.currentFormData = selectedForm
          ? selectedForm.formData
          : null;
        state.data.tempFormData = null;
        state.data.isDirty = false;
      });
    },
    addForm: (form: any) => {
      set((state: any) => {
        state.data.forms.push(form);
      });
    },
    updateForm: (formId: string, formData: any) => {
      set((state: any) => {
        const formIndex = state.data.forms.findIndex(
          (f: any) => f.formId === formId,
        );
        if (formIndex !== -1) {
          state.data.forms[formIndex].formData = formData;
        }
      });
    },
    deleteForm: (formId: string) => {
      set((state: any) => {
        state.data.forms = state.data.forms.filter(
          (f: any) => f.formId !== formId,
        );
      });
    },
    setCurrentFormData: (data: FormConfigType) => {
      set((state: any) => {
        state.data.currentFormData = data;
        state.data.tempFormData = null;
        state.data.isDirty = false;
      });
    },
    updateTempFormData: (updates: Partial<FormConfigType>) => {
      set((state: any) => {
        const currentTemp =
          state.data.tempFormData || state.data.currentFormData;
        state.data.tempFormData = {
          ...currentTemp,
          ...updates,
        };
        state.data.isDirty = true;
      });
    },
    saveChanges: () => {
      set((state: any) => {
        if (state.data.tempFormData) {
          state.data.currentFormData = state.data.tempFormData;
          state.data.tempFormData = null;
          state.data.isDirty = false;
        }
      });
    },
    discardChanges: () => {
      set((state: any) => {
        state.data.tempFormData = null;
        state.data.isDirty = false;
      });
    },
    resetForm: () => {
      set((state: any) => {
        state.data.currentFormData = null;
        state.data.tempFormData = null;
        state.data.isDirty = false;
      });
    },
    setSelectedFormBuilderReportTemplate: (templateId: string) => {
      set((state: any) => {
        state.data.selectedFormBuilderReportTemplate = templateId;
      });
    },
    reorderForms: (newFormsOrder: any[]) => {
      set((state: any) => {
        state.data.forms = newFormsOrder;
      });
    },
  },
});
